import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { trackVisitEvent } from '../components/SiteVisitTracker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faCheck,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faKey,
  faRightToBracket,
  faPen,
  faGift,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import { parsePhoneNumber } from 'libphonenumber-js';
import { DEMO_COURSES } from '../data/courses';
import './FreeTrial.css';

const STEPS = ['Contact', 'Student', 'Time', 'Finish'];

/* Referral: a friend's link (?ref=KARIM47). Kept for 30 days in case the parent
   looks around the site first and books later. */
const REF_KEY = 'rm_ref';
const REF_DAYS = 30;
const readRef = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    const code = String(fromUrl || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
    if (code) {
      localStorage.setItem(REF_KEY, JSON.stringify({ code, at: Date.now() }));
      return code;
    }
    const saved = JSON.parse(localStorage.getItem(REF_KEY) || 'null');
    if (saved?.code && Date.now() - saved.at < REF_DAYS * 86400000) return saved.code;
  } catch {
    /* storage blocked */
  }
  return '';
};
const SUCCESS_STEP = 5;
const AGES = Array.from({ length: 14 }, (_, i) => i + 5); // 5 to 18, same as before
const ADULT_AGE = 19; // "Over 18" is saved as 19
const ageText = (age) => (Number(age) >= ADULT_AGE ? 'over 18' : `${age} years`);

const TRIAL_FLOW = [
  { title: 'Meet the teacher', text: 'A friendly hello and a quick check of your child’s level.' },
  { title: 'Build something', text: 'Your child makes a small project live, step by step.' },
  { title: 'Get a clear path', text: 'We suggest the right course. No pressure to enroll.' },
];

// Course the parent is interested in (optional). '' = not sure, the teacher recommends.
// Juniors (9+, same split as the demo approval) can pick a course; Littles don't see the question.
const isJunior = (age) => parseInt(age, 10) >= 9;

const pad = (n) => String(n).padStart(2, '0');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  try {
    return parsePhoneNumber(phoneNumber).isValid();
  } catch {
    return false;
  }
};

// Groups slots by the parent's LOCAL day (the old code grouped by UTC day,
// so late-evening slots could show under the wrong date).
const groupSlots = (data) => {
  const now = new Date();
  const groups = {};
  data
    .map((s) => ({ iso: s.DateTimeOfAvailable, d: new Date(s.DateTimeOfAvailable) }))
    .filter((s) => !Number.isNaN(s.d.getTime()) && s.d > now)
    .sort((a, b) => a.d - b.d)
    .forEach(({ iso, d }) => {
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
          dayNum: d.getDate(),
          month: d.toLocaleDateString('en-US', { month: 'short' }),
          label: d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' }),
          slots: [],
        };
      }
      groups[key].slots.push({
        iso,
        time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      });
    });
  return Object.values(groups);
};

const FreeTrial = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    phone: '',
    phone1: '',
    age: '',
    hasDevice: '',
    firstName: '',
    lastName: '',
    email: '',
    course: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [dayKey, setDayKey] = useState(null);
  const [slot, setSlot] = useState(null); // { iso, time }
  const [agreed, setAgreed] = useState(true); // ticked by default: parents used to miss it on the last step
  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slotsError, setSlotsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [invite, setInvite] = useState(null); // { code, firstName } when a friend shared the link
  const cardRef = useRef(null);

  // Invited by a friend? check the code and show who
  useEffect(() => {
    const code = readRef();
    if (!code) return;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referral/code/${encodeURIComponent(code)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.code) setInvite({ code: d.code, firstName: d.firstName });
        else {
          try {
            localStorage.removeItem(REF_KEY); // unknown code: forget it
          } catch {
            /* storage blocked */
          }
        }
      })
      .catch(() => {});
  }, []);

  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
  const EMAIL = process.env.REACT_APP_EMAIL;
  const waUrl = (text) =>
    `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

  const selectedDay = days.find((d) => d.key === dayKey) || null;

  // Available time slots (same endpoint + pixel event as before)
  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/getavailabilitiesnewversion`);
        if (!response.ok) throw new Error('Failed to fetch availabilities');
        const data = await response.json();
        if (window.fbq) window.fbq('track', 'StartTrial');
        setDays(groupSlots(Array.isArray(data) ? data : []));
      } catch (err) {
        console.error('Error fetching availabilities:', err);
        setSlotsError(true);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchAvailabilities();
  }, []);

  // Bring the card top into view only if it scrolled above the screen
  const scrollToCard = () => {
    if (!cardRef.current) return;
    const offset = window.innerWidth <= 768 ? 70 : 100;
    const rect = cardRef.current.getBoundingClientRect();
    if (rect.top < offset) {
      window.scrollTo({ top: rect.top + window.scrollY - offset, behavior: 'smooth' });
    }
  };

  const goTo = (next) => {
    setError('');
    setStep(next);
    scrollToCard();
  };

  const handlePhoneChange = (isValid, value, countryData, fullNumber) => {
    setFormData((prev) => ({ ...prev, phone: fullNumber, phone1: value }));
    setError('');
    if (fullNumber && !validatePhoneNumber(fullNumber)) {
      setPhoneError('Please enter a valid phone number.');
    } else {
      setPhoneError('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.phone || !formData.age || !formData.hasDevice) {
        return 'Please fill in all the fields.';
      }
      if (formData.hasDevice === 'no') {
        return 'A laptop or PC is needed to join the class. Mobile phones can’t be used.';
      }
      if (!validatePhoneNumber(formData.phone)) {
        setPhoneError('Please enter a valid phone number.');
        return 'Please check the phone number.';
      }
    }
    if (step === 2) {
      if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
        return 'Please fill in all the fields.';
      }
      if (!EMAIL_RE.test(formData.email.trim())) {
        return 'Please enter a valid email address.';
      }
    }
    if (step === 3 && !slot) {
      return 'Please pick a day and a time.';
    }
    if (step === 4 && !agreed) {
      return 'Please confirm the details to continue.';
    }
    return '';
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const offsetInHours = -new Date().getTimezoneOffset() / 60;
      const date = new Date(slot.iso);

      // Same payload as before
      const submissionData = {
        phone: formData.phone,
        age: formData.age,
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        parentsemail: formData.email.trim(),
        timeh: date.getUTCHours(),
        timem: date.getUTCMinutes(),
        datey: date.getUTCFullYear(),
        datem: date.getUTCMonth() + 1,
        dated: date.getUTCDate(),
        sharelink: '',
        offsetInHours,
        timezone: formData.timezone,
        course: isJunior(formData.age) ? formData.course || '' : '', // '' = not sure (Littles: no question)
        ref: invite?.code || '', // friend's referral code
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/freetrial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) throw new Error('submit failed');

      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', { content_name: 'Free Trial' });
      }
      trackVisitEvent('demo_request'); // Visitors tab: this visit booked a demo
      goTo(SUCCESS_STEP);
    } catch (err) {
      setError('We couldn’t book the class right now. Please try again, or message us on WhatsApp.');
      showProblem(null); // scroll to the message
    } finally {
      setLoading(false);
    }
  };

  // the first field that needs attention on this step (its id), to scroll the parent to it
  const problemField = () => {
    if (step === 1) {
      if (!formData.phone || !validatePhoneNumber(formData.phone)) return 'rmft-phone';
      if (!formData.age) return 'rmft-age';
      if (!formData.hasDevice || formData.hasDevice === 'no') return 'rmft-device';
    }
    if (step === 2) {
      if (!formData.firstName.trim()) return 'rmft-first';
      if (!formData.lastName.trim()) return 'rmft-last';
      if (!formData.email.trim() || !EMAIL_RE.test(formData.email.trim())) return 'rmft-email';
    }
    if (step === 4 && !agreed) return 'rmft-agree';
    return null;
  };

  // show the parent what's missing: scroll to it (middle of the screen), flash it, and put the cursor there
  const showProblem = (fieldId) => {
    setTimeout(() => {
      const field = fieldId ? document.getElementById(fieldId) : null;
      const target = field || cardRef.current?.querySelector('.rmft-error');
      if (!target) return;
      // scroll to the whole group (label + field), but glow on the field only (not the label)
      (target.closest('.rmft-group') || target).scrollIntoView({ behavior: 'smooth', block: 'center' });
      const box =
        target.closest('.rmft-phone') || // phone: the whole box with the flag
        target.querySelector?.('.rmft-choice-row') || // laptop question: the Yes / No buttons
        target;
      box.classList.remove('rmft-flash');
      box.getBoundingClientRect(); // restart the flash if it was already there
      box.classList.add('rmft-flash');
      setTimeout(() => box.classList.remove('rmft-flash'), 1600);
      if (field && /^(INPUT|SELECT)$/.test(field.tagName)) {
        try {
          field.focus({ preventScroll: true });
        } catch {
          /* old browsers */
        }
      }
    }, 60);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const problem = validateStep();
    if (problem) {
      setError(problem);
      showProblem(problemField());
      return;
    }
    if (step === 4) handleSubmit();
    else goTo(step + 1);
  };

  const slotLabel = selectedDay && slot ? `${selectedDay.label} at ${slot.time}` : '';

  // ---------- Step content ----------
  const renderStep = () => {
    if (step === 1) {
      return (
        <>
          <div className="rmft-group">
            <label className="rmft-label" htmlFor="rmft-phone">
              Parent’s phone (WhatsApp)
            </label>
            <div className="rmft-phone">
              <IntlTelInput
                fieldId="rmft-phone"
                preferredCountries={['lb', 'us', 'gb', 'fr', 'ae', 'sa']}
                defaultCountry="lb"
                containerClassName="intl-tel-input"
                inputClassName="rmft-phone-input"
                utilsScript="https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js"
                onPhoneNumberChange={handlePhoneChange}
                format
                autoPlaceholder="polite"
                separateDialCode
                allowDropdown
                value={formData.phone1}
                telInputProps={{ autoComplete: 'tel' }}
              />
            </div>
            {phoneError && <p className="rmft-field-error">{phoneError}</p>}
          </div>

          <div className="rmft-group">
            <label className="rmft-label" htmlFor="rmft-age">
              Student’s age
            </label>
            <select id="rmft-age" name="age" className="rmft-input rmft-select" value={formData.age} onChange={handleChange}>
              <option value="">Select age</option>
              {AGES.map((age) => (
                <option key={age} value={age}>
                  {age} years
                </option>
              ))}
              <option value={ADULT_AGE}>Over 18</option>
            </select>
          </div>

          <fieldset id="rmft-device" className="rmft-group rmft-fieldset">
            <legend className="rmft-label">Do you have a laptop or PC?</legend>
            <div className="rmft-choice-row">
              {['yes', 'no'].map((v) => (
                <label key={v} className={`rmft-choice ${formData.hasDevice === v ? 'is-on' : ''}`}>
                  <input type="radio" name="hasDevice" value={v} checked={formData.hasDevice === v} onChange={handleChange} />
                  {v === 'yes' ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
            {formData.hasDevice === 'no' ? (
              <div className="rmft-nodevice" role="alert">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <div>
                  <strong>A laptop or PC is required</strong>
                  <p>
                    The class can’t be joined from a mobile phone. Your child needs a laptop, a desktop PC,
                    or an iPad with an external mouse. Once you have one, choose “Yes” to continue.
                  </p>
                </div>
              </div>
            ) : (
              <p className="rmft-hint">A computer, or an iPad with an external mouse, is needed for the class.</p>
            )}
          </fieldset>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <div className="rmft-row">
            <div className="rmft-group">
              <label className="rmft-label" htmlFor="rmft-first">
                Student’s first name
              </label>
              <input id="rmft-first" name="firstName" className="rmft-input" autoComplete="off" value={formData.firstName} onChange={handleChange} />
            </div>
            <div className="rmft-group">
              <label className="rmft-label" htmlFor="rmft-last">
                Student’s last name
              </label>
              <input id="rmft-last" name="lastName" className="rmft-input" autoComplete="off" value={formData.lastName} onChange={handleChange} />
            </div>
          </div>
          <div className="rmft-group">
            <label className="rmft-label" htmlFor="rmft-email">
              Parent’s email
            </label>
            <input
              id="rmft-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              className="rmft-input"
              placeholder="name@example.com"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          {isJunior(formData.age) && (
            <div className="rmft-group">
              <label className="rmft-label" htmlFor="rmft-course">
                Course for the free class <span className="rmft-optional">(optional)</span>
              </label>
              <select id="rmft-course" name="course" className="rmft-input rmft-select" value={formData.course} onChange={handleChange}>
                <option value="">Not sure, the teacher will recommend</option>
                {DEMO_COURSES.map((c) => (
                  <option key={c.title} value={c.title}>
                    {c.title}
                  </option>
                ))}
              </select>
              <p className="rmft-hint">Have a course in mind? The teacher will prepare the free class around it.</p>
            </div>
          )}
        </>
      );
    }

    if (step === 3) {
      if (loadingSlots) {
        return (
          <div className="rmft-state">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading available times…
          </div>
        );
      }
      if (slotsError || days.length === 0) {
        return (
          <div className="rmft-state rmft-state--box">
            <p>{slotsError ? 'We couldn’t load the times right now.' : 'This week’s free trial classes are fully booked.'}</p>
            <span className="rmft-state-sub">
              {slotsError
                ? 'Please try again in a few minutes.'
                : 'New times open regularly, so please try again later, or contact us and we’ll arrange a suitable date for you.'}
            </span>
            <a
              className="rmft-wa rmft-wa--sm"
              href={waUrl(`Hi! The free trial times are fully booked this week. Can we arrange a date for my child (age ${ageText(formData.age)})?`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Contact us
            </a>
          </div>
        );
      }
      return (
        <>
          <p className="rmft-tz">
            <FontAwesomeIcon icon={faGlobe} /> Times shown in your time zone ({formData.timezone})
          </p>

          <p className="rmft-label">Pick a day</p>
          <div className="rmft-days" role="radiogroup" aria-label="Day">
            {days.map((d) => (
              <button
                type="button"
                key={d.key}
                role="radio"
                aria-checked={dayKey === d.key}
                className={`rmft-day ${dayKey === d.key ? 'is-on' : ''}`}
                onClick={() => {
                  setDayKey(d.key);
                  setSlot(null);
                  setError('');
                }}
              >
                <span>{d.weekday}</span>
                <strong>{d.dayNum}</strong>
                <span>{d.month}</span>
              </button>
            ))}
          </div>

          {selectedDay && (
            <>
              <p className="rmft-label">Pick a time</p>
              <div className="rmft-times" role="radiogroup" aria-label="Time">
                {selectedDay.slots.map((s) => (
                  <button
                    type="button"
                    key={s.iso}
                    role="radio"
                    aria-checked={slot?.iso === s.iso}
                    className={`rmft-time ${slot?.iso === s.iso ? 'is-on' : ''}`}
                    onClick={() => {
                      setSlot(s);
                      setError('');
                    }}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      );
    }

    // step 4: confirm
    return (
      <>
        <div className="rmft-when">
          <span>Free class</span>
          <strong>{slotLabel}</strong>
          <small>{formData.timezone} · about 50 minutes on Zoom</small>
        </div>

        <dl className="rmft-summary">
          <div>
            <dt>Student</dt>
            <dd>
              {formData.firstName} {formData.lastName}, {ageText(formData.age)}
            </dd>
          </div>
          {isJunior(formData.age) && (
            <div>
              <dt>Course</dt>
              <dd>{formData.course || 'Not sure, the teacher will recommend'}</dd>
            </div>
          )}
          <div>
            <dt>Phone</dt>
            <dd>{formData.phone}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{formData.email}</dd>
          </div>
        </dl>

        <div className="rmft-edit">
          <button type="button" onClick={() => goTo(1)}>
            <FontAwesomeIcon icon={faPen} /> Edit details
          </button>
          <button type="button" onClick={() => goTo(3)}>
            <FontAwesomeIcon icon={faPen} /> Change time
          </button>
        </div>

        <label id="rmft-agree" className="rmft-agree">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => {
              setAgreed(e.target.checked);
              setError('');
            }}
          />
          <span>
            The details are correct and I agree to the{' '}
            <Link to="/terms" target="_blank">
              terms
            </Link>
            .
          </span>
        </label>
      </>
    );
  };

  const nextLabel = step === 4 ? 'Finish booking' : step === 3 ? 'Confirm time' : 'Next';

  return (
    <section className="rmft" id="free-trial-form" aria-label="Book a free trial class">
      <div className="rmft-inner">
        {/* ---------- Booking card ---------- */}
        <div className="rmft-card" ref={cardRef}>
          {step < SUCCESS_STEP ? (
            <form onSubmit={onSubmit} noValidate>
              <ol className="rmft-steps" aria-label="Booking steps">
                {STEPS.map((label, i) => {
                  const n = i + 1;
                  const state = step > n ? 'is-done' : step === n ? 'is-on' : '';
                  return (
                    <li key={label} className={`rmft-step ${state}`} aria-current={step === n ? 'step' : undefined}>
                      <span className="rmft-step-num">{step > n ? <FontAwesomeIcon icon={faCheck} /> : n}</span>
                      <span className="rmft-step-label">{label}</span>
                    </li>
                  );
                })}
              </ol>

              <h2 className="rmft-title">
                {step === 1 && 'Let’s book your free class'}
                {step === 2 && 'Who is the class for?'}
                {step === 3 && 'Pick a time that suits you'}
                {step === 4 && 'Last step: check and finish'}
              </h2>

              {invite && (
                <p className="rmft-invite">
                  <FontAwesomeIcon icon={faGift} />
                  <span>
                    Invited by <b>{invite.firstName}</b>. If you join after the free class, you get <b>10% off</b> your first package.
                  </span>
                </p>
              )}

              {renderStep()}

              {error && (
                <p className="rmft-error" role="alert">
                  {error}
                </p>
              )}

              <div className="rmft-nav">
                {step > 1 && (
                  <button type="button" className="rmft-back" onClick={() => goTo(step - 1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="rmft-next"
                  disabled={loading || (step === 1 && formData.hasDevice === 'no') || (step === 3 && (loadingSlots || slotsError || days.length === 0))}
                >
                  {loading ? (
                    <span className="rmft-spinner" aria-label="Booking" />
                  ) : (
                    <>
                      {nextLabel}
                      <FontAwesomeIcon icon={step === 4 ? faCheck : faArrowRight} />
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="rmft-success" role="status">
              <span className="rmft-success-icon">
                <FontAwesomeIcon icon={faCheck} />
              </span>
              <h2 className="rmft-title">Your free class is booked!</h2>
              <p className="rmft-success-when">{slotLabel}</p>
              <p className="rmft-success-text">
                We’ll message you on WhatsApp to confirm and send the class code. On the day, open{' '}
                <strong>Join Free Class</strong> and enter the code.
              </p>
              <div className="rmft-success-actions">
                <a
                  className="rmft-wa"
                  href={waUrl(`Hi! I just booked a free trial class for ${formData.firstName} on ${slotLabel}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsapp} /> Message us on WhatsApp
                </a>
                <Link to="/" className="rmft-home">
                  Back to home
                </Link>
              </div>
              {EMAIL && (
                <p className="rmft-success-help">
                  Questions? <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </p>
              )}
            </div>
          )}
        </div>

        {/* ---------- Side info ---------- */}
        <aside className="rmft-side">
          <h2 className="rmft-side-title">What happens in the free class</h2>
          <ol className="rmft-flow">
            {TRIAL_FLOW.map((item, i) => (
              <li key={item.title}>
                <span className="rmft-flow-num">{i + 1}</span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <a
            className="rmft-help"
            href={waUrl('Hi! I have a question about the free trial class.')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> Questions? Chat with us
          </a>

          <div className="rmft-switch">
            <Link to="/joinfreeclass" className="rmft-switch-link">
              <FontAwesomeIcon icon={faKey} />
              <span>
                <strong>Already booked?</strong> Enter your class code
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="rmft-switch-arrow" />
            </Link>
            <Link to="/login" className="rmft-switch-link">
              <FontAwesomeIcon icon={faRightToBracket} />
              <span>
                <strong>Already enrolled?</strong> Students sign in
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="rmft-switch-arrow" />
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default FreeTrial;